<template>
    <AppModal :title="$t('labels.aiTranslateContent')" ref="modal" @on-hide="hide" size="lg">
        <div class="modal-body">
            <template v-if="task.state.isRunning">
                <TaskProgressBar :done="task.state.done" :total="task.state.total"/>
            </template>
            <template v-else-if="!resultTranslations">
                <ContentTranslationForm
                    :languages-aliases="Object.keys(languages)"
                    :target-languages="targetLanguages"
                    :languages="languages"
                    v-model:source-language="sourceLanguage"
                    v-model:fieldsForTranslation="fieldsForTranslation"
                    v-model:selected-target-languages="selectedTargetLanguages"
                />
            </template>
            <template v-else-if="resultTranslations">
                <ContentTranslationResultView :source-language="sourceLanguage" :target-languages="selectedTargetLanguages" :product="product" :fields-for-translation="fieldsForTranslation.filter(field => field.selected).map(field => field.value)" :result-translations="resultTranslations" />
            </template>
            <Alert v-if="tokenBalance !== null && tokenBalance <= 0 && !task.state.isRunning" type="warning" class="mb-0 mr-2 flex-grow-1">
                {{ $t('alerts.insufficient_ai_balance') }}
            </Alert>
        </div>
        <div class="modal-footer" v-if="!task.state.isRunning">
            <AppButton color="indigo" @click="generateTranslations" v-if="!resultTranslations" :disabled="selectedTargetLanguages.length === 0 || (tokenBalance !== null && tokenBalance <= 0)">
                {{ $t('buttons.translate') }}
            </AppButton>
            <AppButton color="indigo" @click="editTranslations" v-else>
                {{ $t('buttons.edit') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../../../../components/new/modal/AppModal.vue';
import AppButton from '../../../../../../../components/new/button/AppButton.vue';
import Alert from '../../../../../../../components/AlertComponent.vue';
import TaskProgressBar from '../../../../../../../components/new/progress/TaskProgressBar.vue';
import ContentTranslationForm from './ContentTranslationForm.vue';
import ContentTranslationResultView from './ContentTranslationResultView.vue';

import GeminiService from '../../../../../../../services/GeminiService/GeminiService';
import { httpClient } from '../../../../../../../services/HttpClient';
import MessageNotificator from '../../../../../../../services/MessageNotificator';
import { NotValidJsonStringError } from '../../../../../../../services/Errors/NotValidJsonStringError';
import { TooManyRequestsError } from '../../../../../../../services/Errors/TooManyRequestsError';
import { ServiceUnavailableError } from '../../../../../../../services/Errors/ServiceUnavailableError';
import { createProgressTask } from '../../../../../../../services/Progress/ProgressTask';

export default {
    name: "ContentTranslationModal",
    components: {
        AppModal, AppButton, Alert, TaskProgressBar, ContentTranslationForm, ContentTranslationResultView
    },
    props: {
        languagesAliases: {
            type: Array,
            default: () => []
        },
        product: {
            type: Object,
            required: true
        },
        selectedLanguageAlias: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            task: createProgressTask(),
            languages: [],
            sourceLanguage: null,
            resultTranslations: null,
            fieldsForTranslation: [
                { label: this.$t('labels.name'), value: 'names', selected: true },
                { label: this.$t('labels.description'), value: 'descriptions', selected: true },
                { label: this.$t('labels.seo'), value: 'seos', selected: true }
            ],
            selectedTargetLanguages: [],
            tokenBalance: null
        }
    },
    methods: {
        show() {
            this.fetchBalance();
            const preferred = this.selectedLanguageAlias
                ? this.languages.find(lang => lang.value === this.selectedLanguageAlias)
                : null;
            this.sourceLanguage = preferred ?? this.languages[0];
            this.$refs.modal.show()
        },
        hide() {
            this.task.cancel();
            this.$refs.modal.hide()
            this.resultTranslations = null;
            this.tokenBalance = null;
            this.fieldsForTranslation.forEach(field => field.selected = true);
        },
        async fetchBalance() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`);
            this.tokenBalance = response.data.balance;
        },
        generateTranslations() {
            const geminiService = new GeminiService();

            return this.task.run(async () => {
                await this.generateTranslationsForProduct(geminiService, this.product);
                await geminiService.flushTokens(this.$route.params.projectUuid, `Переклад контенту товару ${this.product.names[this.sourceLanguage.value] || Object.values(this.product.names)[0] || ''}`);
                await this.fetchBalance();
            });
        },
        async generateTranslationsForProduct(geminiService, product) {
            const prompt = this.createPrompt();

            try {
                const geminiResponse = await geminiService.generateWithRetry(prompt);

                this.resultTranslations = {
                    names: geminiResponse.names || {},
                    descriptions: geminiResponse.descriptions || {},
                    seos: geminiResponse.seos || {}
                };
            } catch (error) {
                if (error instanceof NotValidJsonStringError) {
                    new MessageNotificator().error(this.$t('alerts.received_not_valid_json'));
                } else if (error instanceof TooManyRequestsError) {
                    new MessageNotificator().error(this.$t('alerts.too_many_requests'));
                } else if (error instanceof ServiceUnavailableError) {
                    new MessageNotificator().error(this.$t('alerts.service_unavailable'));
                } else {
                    new MessageNotificator().error(this.$t('alerts.contact_support'));
                }
            }
        },
        createPrompt() {
            let prompt = `Translate the following product content from ${this.sourceLanguage.label} to the following languages: `;

            const targetLangs = this.targetLanguages
                .map(lang => lang.label)
                .join(', ');

            prompt += targetLangs + '. ';

            prompt += '\nContent to translate: ';

            this.fieldsForTranslation
                .filter(field => field.selected)
                .forEach(field => {
                    prompt += `\n${field.value}: ${JSON.stringify(this.product[field.value][this.sourceLanguage.value]) || ''}`;
                });

            prompt += '\nThe translated content should be in JSON format with field name keys.';
            prompt += '\nExample response: ';
            prompt += '\n{ "names": { "fr": "Nom en français", "de": "Name auf Deutsch" }, "descriptions": { "fr": "Description en français", "de": "Beschreibung auf Deutsch" } }';

            return prompt;
        },
        editTranslations() {
            httpClient.patch(`/integrations/products/${this.product.uuid}`, this.resultTranslations).then((response) => {
                this.$emit('product-updated', response.data.data);
                new MessageNotificator().success(this.$t('alerts.success'));
            });
            this.hide();
        }
    },
    computed: {
        targetLanguages() {
            return this.languages.filter(lang => lang.value !== this.sourceLanguage?.value);
        }
    },
    watch: {
        sourceLanguage(newValue) {
            this.selectedTargetLanguages = this.languages
                .filter(lang => lang.value !== newValue?.value)
        }
    },
    mounted() {
        this.languages = this.languagesAliases.map(alias => ({
            label: this.$t(`languages.${alias}`),
            value: alias
        }));
    },
    beforeUnmount() {
        this.task.cancel();
    }
}
</script>
